import React from "react";
import { MessageCircle, MapPin, Plane } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const WA_BASE = "https://wa.me/32498737919";

/**
 * "Interventions Sur-Mesure & Déplacements" — Blue Ocean strategy block
 * inserted on Géobiologie / Magnétisme / Cuisine Vivante.
 *
 * @param {"geobiologie"|"magnetisme"|"cuisine"} pillar - used to pre-fill the WhatsApp message.
 */
export const TravelSection = ({ pillar = "geobiologie" }) => {
  const { locale } = useLang();
  const isFr = locale === "fr";

  const subjectFr = {
    geobiologie: "Demande d'étude — Déplacement géobiologie",
    magnetisme: "Demande d'étude — Déplacement magnétisme",
    cuisine: "Demande d'étude — Déplacement accompagnement holistique",
  }[pillar] || "Demande d'étude — Déplacement";

  const subjectEn = {
    geobiologie: "Travel study request — Geobiology",
    magnetisme: "Travel study request — Magnetism",
    cuisine: "Travel study request — Holistic accompaniment",
  }[pillar] || "Travel study request";

  const message = isFr
    ? `${subjectFr}\n\nBonjour Fabrice, je souhaite une étude de déplacement pour une intervention FACOW360. Voici les éléments :\n• Lieu d'intervention :\n• Type d'intervention :\n• Dates envisagées :\n• Contraintes spécifiques :`
    : `${subjectEn}\n\nHello Fabrice, I would like a travel study for a FACOW360 intervention. Details:\n• Location:\n• Type of intervention:\n• Possible dates:\n• Specific constraints:`;

  const waUrl = `${WA_BASE}?text=${encodeURIComponent(message)}`;

  return (
    <section
      className="mt-16 scroll-mt-28"
      id="deplacements"
      data-testid={`travel-section-${pillar}`}
    >
      <div className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1128]/80 via-[#0F1A3A]/60 to-[#D4AF37]/5 backdrop-blur-sm p-8 lg:p-12">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-[#00F0FF]/5 blur-3xl pointer-events-none" />

        <div className="relative">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] font-mono-tech uppercase tracking-[0.25em]">
            <Plane size={12} /> {isFr ? "Stratégie sur-mesure" : "Bespoke strategy"}
          </span>

          <h2 className="mt-4 font-display text-2xl md:text-3xl text-cyan-glow leading-tight tracking-tight">
            {isFr ? "Interventions Sur-Mesure & Déplacements" : "Bespoke Interventions & Travel"}
          </h2>

          <div className="mt-5 max-w-3xl text-slate-300 leading-relaxed text-base space-y-4">
            <p>
              {isFr
                ? "Mon ancrage principal se situe entre Verviers, Liège et Narbonne. Toutefois, pour répondre à des demandes spécifiques d'harmonisation de l'habitat (géobiologie), de rééquilibrage énergétique (magnétisme) ou d'accompagnement holistique global, je me déplace partout en Europe sur demande."
                : "My main anchor lies between Verviers, Liège and Narbonne. However, to address specific requests for habitat harmonisation (geobiology), energetic rebalancing (magnetism) or comprehensive holistic accompaniment, I travel anywhere in Europe on request."}
            </p>
            <p>
              {isFr
                ? "Les modalités logistiques (transport et hébergement) sont étudiées ensemble et font l'objet d'un devis transparent, intégralement pris en charge par le client."
                : "Logistical arrangements (transport and accommodation) are studied together and lead to a transparent quote, fully covered by the client."}
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-4">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold"
              data-testid={`travel-cta-whatsapp-${pillar}`}
            >
              <MessageCircle size={14} />{" "}
              {isFr ? "Demander une étude pour un déplacement" : "Request a travel study"}
            </a>
            <a
              href="mailto:contact@facow360.com?subject=FACOW360%20%E2%80%94%20Demande%20de%20d%C3%A9placement"
              className="btn-primary"
              data-testid={`travel-cta-email-${pillar}`}
            >
              {isFr ? "Par email" : "By email"}
            </a>
          </div>

          <p className="mt-6 inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500">
            <MapPin size={11} className="text-[#D4AF37]" />
            {isFr
              ? "Verviers · Liège (Belgique) — Narbonne (France) — Europe sur demande"
              : "Verviers · Liège (Belgium) — Narbonne (France) — Europe on request"}
          </p>
        </div>
      </div>
    </section>
  );
};
